import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { ExceptionCorrectionItemModelDto } from '@treasury/api/channel';
import { CheckException } from '@treasury/domain/arp';
import { ExceptionCorrection } from '@treasury/domain/arp/exception-correction.entity';
import { toBase64 } from '@treasury/domain/utilities/file-handling';
import dateFormatter from '@treasury/policy/lib/formatters/date.formatter';
import usd from '@treasury/policy/lib/formatters/usd.formatter';
import { TmBaseComponent } from '@treasury/presentation';
import { attachIcon } from '@treasury/presentation/assets/icons';
import { TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-currency-field';
import '@treasury/presentation/components/tm-date-picker';
import '@treasury/presentation/components/tm-number-field';
// eslint-disable-next-line no-duplicate-imports
import type { TmNumberFieldChanged } from '@treasury/presentation/components/tm-number-field';
import '@treasury/presentation/components/tm-text-field';
import { format } from 'date-fns';
import { type PropertyValueMap, type TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../../../services/jhd-header-bar.service';
import {
    CheckCorrection,
    CheckCorrectionLabel,
    CheckCorrectionType,
    CheckCorrectionTypeList,
} from '../../../types';
import '../components/header-text.component';
import '../components/select-from-list.component';
import './check-exception-comment-editor';

export const tagName = 'check-exception-correction';
@customElement(tagName)
export class CheckExceptionCorrection extends TmBaseComponent {
    @property()
    public checkException?: CheckException;

    @property()
    public correctionPayloadSetter?: (correction: ExceptionCorrection) => void;

    @property()
    public reviewingCorrectionRequest = false;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @state()
    private availableCorrections = CheckCorrectionTypeList;

    @state()
    private corrections: CheckCorrection<string>[] = [
        {
            correctedValue: '',
            label: CheckCorrectionLabel.None,
        },
    ];

    @state()
    private comment = '';

    @state()
    private correctionsAreValid = false;

    private attachment?: File;

    public render() {
        if (this.reviewingCorrectionRequest) {
            return this.renderReviewCorrections();
        }
        return html`<tm-section>
            ${this.renderEditableCorrections()} ${this.renderCommentEditor()}
        </tm-section>`;
    }

    protected async updated(
        _changedProperties: PropertyValueMap<CheckExceptionCorrection> | Map<PropertyKey, unknown>
    ) {
        // whenever the component is advanced to the review step, it sends the formatted correction
        // back up to the parent component for the correction submit api request
        if (
            _changedProperties.has('reviewingCorrectionRequest') &&
            this.reviewingCorrectionRequest &&
            this.correctionPayloadSetter
        ) {
            this.correctionPayloadSetter(await this.getFormattedCorrection());
        }
    }

    private setCorrectionsAreValid() {
        this.correctionsAreValid = this.corrections.every(correction => {
            if (correction.type === CheckCorrectionType.Duplicate) {
                return true;
            }
            return !!correction.type && !!correction.correctedValue;
        });
        this.dispatchEvent(
            new CustomEvent('correction-changed', {
                detail: { isValid: this.correctionsAreValid },
            })
        );
    }

    private async getFormattedCorrection() {
        if (!this.checkException) {
            throw new Error('Could get formatted correct. No check exception available.');
        }

        const exceptionCorrectionItems: ExceptionCorrectionItemModelDto[] = this.corrections
            .map(correction => {
                return {
                    exceptionCorrectionItem: this.getExceptionNumberFromType(correction.type),
                    correctionValue: correction.correctedValue,
                };
            })
            .filter(item => item.exceptionCorrectionItem > 0);

        const formattedCorrection = new ExceptionCorrection({
            arpExceptionDetailUniqueId: this.checkException.guid,
            comment: this.comment,
            exceptionCorrectionItems,
        });

        if (this.attachment) {
            formattedCorrection.attachment = await toBase64(this.attachment);
            formattedCorrection.attachmentFileName = this.attachment?.name ?? '';
        }

        return formattedCorrection;
    }

    private printValue(val: string, type?: CheckCorrectionType) {
        switch (type) {
            case CheckCorrectionType.DatePosted:
                return dateFormatter(val);
            case CheckCorrectionType.Amount:
                return usd(Number.parseFloat(val));
            case CheckCorrectionType.CheckNumber:
            case CheckCorrectionType.Duplicate:
            case CheckCorrectionType.Payee:
            case CheckCorrectionType.AccountNumber:
            default:
                return val;
        }
    }

    private getExceptionNumberFromType(type?: CheckCorrectionType): number {
        switch (type) {
            case CheckCorrectionType.AccountNumber:
                return 1;
            case CheckCorrectionType.DatePosted:
                return 2;
            case CheckCorrectionType.CheckNumber:
                return 3;
            case CheckCorrectionType.Amount:
                return 4;
            case CheckCorrectionType.Payee:
                return 5;
            case CheckCorrectionType.Duplicate:
                return 6;
            default:
                return 0;
        }
    }

    private setCorrectedValue(type: CheckCorrectionType, val: string) {
        const correction = this.corrections.find(item => item.type === type);
        if (correction) {
            correction.correctedValue = val;
        }
    }

    private addNewCorrection() {
        const corrections = [...this.corrections];
        if (corrections.length < CheckCorrectionTypeList.length) {
            corrections.push({
                correctedValue: '',
                label: CheckCorrectionLabel.None,
            });
        }
        this.corrections = corrections;
    }

    private addCorrectionText(label: CheckCorrectionLabel) {
        if (label === CheckCorrectionLabel.None) {
            return label;
        }
        return `Correct ${label}`;
    }

    private capitalizeCorrectionLabel(label: CheckCorrectionLabel) {
        const firstLetter = label.slice(0, 1);
        return `${firstLetter.toUpperCase()}${label.slice(1)}`;
    }

    private getCorrectionLabelFromType(type: CheckCorrectionType) {
        switch (type) {
            case CheckCorrectionType.AccountNumber:
                return CheckCorrectionLabel.AccountNumber;
            case CheckCorrectionType.DatePosted:
                return CheckCorrectionLabel.DatePosted;
            case CheckCorrectionType.CheckNumber:
                return CheckCorrectionLabel.CheckNumber;
            case CheckCorrectionType.Amount:
                return CheckCorrectionLabel.Amount;
            case CheckCorrectionType.Payee:
                return CheckCorrectionLabel.Payee;
            case CheckCorrectionType.Duplicate:
                return CheckCorrectionLabel.None;
            default:
                return CheckCorrectionLabel.AccountNumber;
        }
    }

    private getExceptionValueFromCorrectionType(type: CheckCorrectionType) {
        if (!this.checkException) {
            return undefined;
        }

        switch (type) {
            case CheckCorrectionType.AccountNumber:
                return this.checkException.accountNumber;
            case CheckCorrectionType.DatePosted:
                return this.checkException.postedDate;
            case CheckCorrectionType.CheckNumber:
                return this.checkException.checkNumber;
            case CheckCorrectionType.Amount:
                return this.checkException.issuedAmount;
            case CheckCorrectionType.Payee:
                return this.checkException.issuedPayee;
            case CheckCorrectionType.Duplicate:
                return false;
            default:
                return undefined;
        }
    }

    private renderCorrectionInputComponentFromType(correction: CheckCorrection): TemplateResult {
        const { type } = correction;
        switch (type) {
            case CheckCorrectionType.DatePosted:
                return html`<tm-date-picker
                    required
                    .initial=${new Date(formatDateStr(correction.correctedValue))}
                    @value-changed=${(e: TmValueChanged<Date>) => {
                        const { value, isValid } = e.detail;
                        if (!isValid || !value) {
                            return;
                        }

                        const formattedDate = formatDateStr(value.toString());
                        this.setCorrectedValue(CheckCorrectionType.DatePosted, formattedDate);
                        this.setCorrectionsAreValid();
                    }}
                ></tm-date-picker>`;
            case CheckCorrectionType.Amount:
                return html`<tm-currency-field
                    required
                    .initial=${correction.correctedValue}
                    @value-changed=${(e: TmValueChanged<string>) => {
                        const { value } = e.detail;
                        if (!value) {
                            return;
                        }

                        this.setCorrectedValue(CheckCorrectionType.Amount, value);
                        this.setCorrectionsAreValid();
                    }}
                ></tm-currency-field>`;
            case CheckCorrectionType.Duplicate:
                return html`${nothing}`;
            case CheckCorrectionType.CheckNumber:
                return html`<tm-number-field
                    required
                    .initial=${parseInt(correction.correctedValue, 10)}
                    @value-changed=${(e: TmNumberFieldChanged) => {
                        const { value, isValid } = e.detail;
                        if (!value || !isValid) {
                            return;
                        }

                        this.setCorrectedValue(CheckCorrectionType.CheckNumber, value.toString());
                        this.setCorrectionsAreValid();
                    }}
                ></tm-number-field>`;
            case CheckCorrectionType.AccountNumber:
                return html`<tm-text-field
                    required
                    .initial=${correction.correctedValue}
                    @value-changed=${(e: CustomEvent<{ value: string }>) => {
                        this.setCorrectedValue(CheckCorrectionType.AccountNumber, e.detail.value);
                        this.setCorrectionsAreValid();
                    }}
                ></tm-text-field>`;
            case CheckCorrectionType.Payee:
                return html`<tm-text-field
                    required
                    .initial=${correction.correctedValue}
                    @value-changed=${(e: CustomEvent<{ value: string }>) => {
                        this.setCorrectedValue(CheckCorrectionType.Payee, e.detail.value);
                        this.setCorrectionsAreValid();
                    }}
                ></tm-text-field>`;

            default:
                return html`${nothing}`;
        }
    }

    private renderRequestCorrectionButton(correction: CheckCorrection, idx: number) {
        if (!this.checkException) {
            return nothing;
        }

        return html`
            <div class="ml-4 border-b correction-item">
                <div class="flex justify-between py-4 pr-4">
                    <div>Item to correct</div>
                    <tm-slider
                        .header=${html`<header-text
                            .header=${'Items to Correct'}
                            .subheader=${`Check ${this.checkException.checkNumber}`}
                        ></header-text>`}
                        .value=${this.corrections[idx].type}
                    >
                        <select-from-list
                            .list=${this.availableCorrections.filter(
                                (ac: CheckCorrectionType) =>
                                    ac === this.corrections[idx].type ||
                                    !this.corrections
                                        .map((c: CheckCorrection) => c.type)
                                        .includes(ac)
                            )}
                            .changeItem=${(item: CheckCorrectionType) => {
                                const editingCorrection = this.corrections[idx];
                                editingCorrection.type = item;
                                editingCorrection.label = this.getCorrectionLabelFromType(item);
                                editingCorrection.previousValue =
                                    this.getExceptionValueFromCorrectionType(item)?.toString();
                                editingCorrection.correctedValue = '';
                                this.corrections = [...this.corrections];
                                this.setCorrectionsAreValid();
                            }}
                            .selectedItem=${this.corrections[idx].type}
                        ></select-from-list>
                    </tm-slider>
                </div>
                ${correction.type && correction.label
                    ? html`<div class="border-t border-dashed w-full pr-4">
                          <div class="flex justify-between py-4 items-center w-full h-full">
                              <span class="mr-1">${this.addCorrectionText(correction.label)}</span>
                              ${this.renderCorrectionInputComponentFromType(correction)}
                          </div>
                      </div>`
                    : nothing}
            </div>
        `;
    }

    private renderEditableCorrections() {
        const correctionList = this.corrections.map(this.renderRequestCorrectionButton, this);
        if (this.corrections[0].type && this.corrections.length < CheckCorrectionTypeList.length) {
            correctionList.push(
                html`<div class="w-full h-14 flex items-center p-5">
                    <button @click=${this.addNewCorrection} class="text-brand border-0">
                        + Add another correction
                    </button>
                </div> `
            );
        }

        return correctionList;
    }

    private renderCommentEditor() {
        const commentChanged = (e: CustomEvent<string>) => {
            this.comment = e.detail;
        };
        const attachmentChanged = (e: CustomEvent<File>) => {
            this.attachment = e.detail;
        };
        return html`<check-exception-comment-editor
            .commentText=${this.comment}
            .fileName=${this.attachment?.name}
            @value-changed=${commentChanged}
            @attachment-changed=${attachmentChanged}
        ></check-exception-comment-editor>`;
    }

    private renderReviewCorrections() {
        if (!this.checkException) {
            return nothing;
        }

        const corrections = html`${this.corrections.map(correction => {
            if (correction.type === CheckCorrectionType.Duplicate) {
                return html`<div class="font-thin">${correction.type}</div>
                    <hr />`;
            }
            return html`
                <div>
                    <div class="font-thin">${this.capitalizeCorrectionLabel(correction.label)}</div>
                    <div class="font-bold">
                        ${this.printValue(correction.previousValue || '', correction.type)}
                    </div>
                    <div class="font-thin">${this.addCorrectionText(correction.label)}</div>
                    <div class="font-bold">
                        ${this.printValue(correction.correctedValue, correction.type)}
                    </div>
                </div>
                <hr />
            `;
        })}`;

        return html`
            <tm-section>
                <div class="p-6">
                    <div class="mb-4">
                        <div>
                            Account: ${this.checkException.accountNumber}, Check Number:
                            ${this.checkException.checkNumber}
                        </div>
                        <br />
                        <div class="font-bold">Items to Correct</div>
                        ${corrections}
                        <br />
                        <div>Comment</div>
                        <div>${this.comment}</div>
                    </div>
                    <div class="border-t border-[--secondary-text-color] flex items-center py-3">
                        <tm-icon .icon=${attachIcon} class="mr-3"></tm-icon>
                        <div>${this.attachment?.name ?? 'no attachment found'}</div>
                    </div>
                </div>
            </tm-section>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
            `,
        ];
    }
}

function formatDateStr(dateString: string) {
    return format(dateString, 'yyyy-MM-dd');
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CheckExceptionCorrection;
    }
}
